import { useForm } from 'react-hook-form';
import { Organization } from '@tinct/core-api-models';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRemoveOrganization } from '@tinct/core-api';
import { handleApiError } from '@tinct/web-form';
import { toast } from 'sonner';
import * as z from 'zod';

const deleteConfirmationSchema = z.object({
  confirmation: z.string().superRefine((val, ctx) => {
    if (val !== 'DELETE') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please type DELETE to confirm',
      });
    }
  }),
});

type DeleteConfirmation = z.infer<typeof deleteConfirmationSchema>;

type UseDeleteOrganizationFormOptions = {
  organization: Organization;
  onSuccess?: () => void;
};

export const useDeleteOrganizationForm = ({
  organization,
  onSuccess,
}: UseDeleteOrganizationFormOptions) => {
  const form = useForm<DeleteConfirmation>({
    resolver: zodResolver(deleteConfirmationSchema),
    defaultValues: {
      confirmation: '',
    },
  });

  const removeOrganization = useRemoveOrganization({
    onSuccess: () => {
      toast.success('Workspace deletion scheduled');
      onSuccess?.();
    },
    onError: (error) => handleApiError(error, form),
  });

  const handleSubmit = form.handleSubmit(() => {
    if (!organization.id) return;
    removeOrganization.mutate({
      organizationId: organization.id,
    });
  });

  const isConfirmationValid = form.watch('confirmation') === 'DELETE';

  return {
    form,
    handleSubmit,
    isLoading: removeOrganization.isPending,
    isConfirmationValid,
  };
};
